/**
 * Reading and writing the merged document's `project-redaction-data` asset
 * (VE-30104). See `projectRedactionAsset.ts` for what the body holds and why
 * it is an asset rather than a `details` key.
 */
import { fetchGraphQLApi } from '@helpers/callApi';
import type { TDOId } from '@common-modules/universal/models/Brands';

import { serviceDeleteAsset } from '@redact-modules/documents/services';
import {
  buildProjectRedactionAssetDetails,
  buildProjectRedactionBody,
  parseProjectRedactionData,
  PROJECT_REDACTION_ASSET_TYPE,
  type ProjectRedactionData,
} from './projectRedactionAsset';

/**
 * The newest `project-redaction-data` body on a project, or null when the
 * project carries none.
 *
 * NEWEST WINS, since writes are append-only — `limit: 1` on the server's
 * ordering, because the superseded bodies exist for the write path to clean
 * up and not for any reader. Two round trips: the asset carries a `signedUri`
 * and the JSON is fetched from it, as the PII and OCR readers do.
 *
 * NULL ON "NO ASSET", THROWS ON "COULD NOT ASK" — the caller records those as
 * different states, and collapsing them is what left the Bates record saying
 * "carries no stamping" for a whole session after one failed lookup.
 */
export async function serviceLoadProjectRedactionData(
  projectTdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<{ assetId: string; data: ProjectRedactionData } | null> {
  const query = `
    query projectRedactionData($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "${PROJECT_REDACTION_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 1) {
          records { id signedUri details container { id } }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<{
    temporalDataObject?: {
      assets?: {
        records?: Array<{
          id: string;
          signedUri?: string | null;
          container?: { id?: string | null } | null;
        }>;
      } | null;
    } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: projectTdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `merged document settings lookup failed: ${String(
        result.errors[0]?.message ?? 'unknown error'
      )}`
    );
  }
  const record = result.data?.temporalDataObject?.assets?.records?.[0];
  if (!record?.signedUri) {
    return null;
  }
  // FAIL CLOSED ON THE CONTAINER, the way the per-document reader does — its
  // own comment notes corruption seen in production. `container?.id &&` would
  // wave through an asset whose container came back null, which is the one
  // shape a restored or hand-written record produces.
  if (record.container?.id !== String(projectTdoId)) {
    throw new Error(
      'merged document settings load: container TDO mismatch (data corruption?)'
    );
  }
  const res = await fetch(record.signedUri);
  if (!res.ok) {
    throw new Error(
      `merged document settings download failed: HTTP ${res.status}`
    );
  }
  const body: unknown = await res.json();
  const data = parseProjectRedactionData(body, record.id);
  // AND THE BODY'S OWN CLAIM about which project it belongs to, so a record
  // copied or restored onto the wrong project is caught by a reader rather
  // than silently marking the wrong release. Absent on bodies written before
  // the field existed, which are trusted as before.
  if (data?.projectTdoId && data.projectTdoId !== String(projectTdoId)) {
    throw new Error(
      'merged document settings load: body names a different project'
    );
  }
  // A body that exists but does not parse is NOT a failure to ask: the asset
  // was read, and what it holds is unusable — a hand-edited record, or one
  // written by a build whose format this one does not support. Reported as
  // "carries none" so the reviewer can write a good one over it, rather than
  // as an error they cannot act on.
  return data ? { assetId: record.id, data } : null;
}

/**
 * Write a new version of a project's record, and retire the ones it replaces.
 *
 * APPEND-ONLY WITH A TIDY-UP: `createAsset` is the only write, so every Apply
 * leaves another body behind and the reader only ever wants the newest.
 *
 * The retire step is BEST EFFORT and runs last. Deleting first would leave a
 * window where a failed create loses the settings entirely, and failing the
 * write over an undeletable stale body would report a success as an error —
 * a leftover body is harmless, so it loses to both.
 *
 * The tidy-up keeps THE NEWEST AND ONE BEHIND IT. It used to keep three older
 * bodies; nothing has ever read one, since the reader asks for `limit: 1`
 * newest-first and that query and this one are the only two places this asset
 * type is read at all.
 *
 * BUT NOT NONE. `serviceLoadProjectRedactionData` carries an explicit branch
 * for a body that exists and does not parse — "a hand-edited record, or one
 * written by a build whose format this one does not support" — so a bad write
 * is a failure this reader already knows happens, and this blob governs the
 * watermarks and Bates on a legal release. With no history there is no way
 * back from one. A single superseded body is one small asset per project and
 * the only manual recovery there is.
 */
const KEEP_SUPERSEDED_BODIES = 1;

export async function serviceWriteProjectRedactionData(
  projectTdoId: TDOId,
  data: ProjectRedactionData,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string
): Promise<{ id: string; createdDateTime?: string }> {
  const query = `
    mutation saveProjectRedactionData($id: ID!, $details: JSONData) {
      createAsset(input: {
        containerId: $id,
        type: "${PROJECT_REDACTION_ASSET_TYPE}",
        contentType: "application/json",
        details: $details
      }) {
        id
        createdDateTime
      }
    }
  `;
  const filename = 'project-redactions.json';
  const formData = new FormData();
  formData.append('query', query);
  // THE STAMPS RIDE IN `details`, not only in the body.
  //
  // `collectBatesBehindTdoIds` runs over every row of the folder view, and it
  // only ever needs to know WHICH write each concern last moved in — never
  // the watermarks themselves or the Bates settings. Keeping that answer in
  // `details` means the asset-list query settles staleness and nothing
  // downloads a body to draw a badge. It was free while Bates lived on the
  // project TDO's own details, and this is what keeps it free.
  formData.append(
    'variables',
    JSON.stringify({
      id: projectTdoId,
      details: buildProjectRedactionAssetDetails(data),
    })
  );
  formData.append('filename', filename);
  formData.append(
    'file',
    new Blob([JSON.stringify(buildProjectRedactionBody(data))], {
      type: 'application/json',
    }),
    filename
  );
  const res = await fetch(`${apiRoot}/${graphQLEndpoint}`, {
    method: 'POST',
    body: formData,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  // Status before parsing: a non-2xx (an auth proxy serving a login page, a
  // gateway timeout, an HTML 500) would otherwise throw a JSON-parse error
  // and lose the status that explains it.
  if (!res.ok) {
    throw new Error(`merged document settings save failed: HTTP ${res.status}`);
  }
  const json = (await res.json()) as {
    data?: {
      createAsset?: { id: string; createdDateTime?: string } | null;
    } | null;
    errors?: Array<{ message?: string }>;
  };
  if (json.errors?.length) {
    throw new Error(
      `merged document settings save failed: ${String(
        json.errors[0]?.message ?? 'unknown error'
      )}`
    );
  }
  const created = json.data?.createAsset;
  if (!created) {
    throw new Error('merged document settings save returned no asset');
  }
  return { id: created.id, createdDateTime: created.createdDateTime };
}

/**
 * Delete every body the one just written replaces. Never throws — see below.
 *
 * SUPERSEDED MEANS OLDER THAN MINE, not "past the first few in the list".
 * That distinction is what makes keeping only the newest safe. Trimming by
 * POSITION would delete a body a concurrent writer had just added — it sorts
 * above ours and is not ours, so a position window of zero takes it — and
 * that reviewer's settings would be gone, not merely overwritten. Comparing
 * timestamps can only ever delete something our own write has already
 * replaced.
 *
 * Ties do not delete. Timestamps at this resolution can tie, and a tie is
 * exactly the concurrent case; strictly-older is the safe reading of it.
 *
 * EVERY RECORD IS RE-CHECKED BEFORE IT IS DELETED. `serviceDeleteAsset` takes
 * a bare id and asks nothing, so without this the whole safety of the loop
 * rests on a server-side `assetType` filter it cannot see — and a regression
 * there would no longer leave three bodies standing, it would delete every
 * older asset on the TDO, which on a project TDO includes the merged PDF.
 * The reader two functions up verifies its container for the same reason and
 * names production corruption as the cause.
 *
 * NO TIMESTAMP, NO DELETE. If the create did not report one there is nothing
 * to compare against, and the right answer is to leave the history alone —
 * a leftover body costs a little storage, and the alternative is guessing.
 *
 * NOT AWAITED BY THE WRITE — exported and forked rather than called at the
 * end of it. A history query plus up to 99 sequential deletes would otherwise
 * sit between `createAsset` returning and the panel saying Updated, for a
 * tidy-up whose premise is that nobody needs it to have happened.
 */
export async function retireSupersededBodies(
  projectTdoId: TDOId,
  justWrittenId: string,
  justWrittenAt: string | undefined,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<void> {
  if (!justWrittenAt) {
    return;
  }
  try {
    const query = `
      query projectRedactionHistory($id: ID!) {
        temporalDataObject(id: $id) {
          assets(assetType: "${PROJECT_REDACTION_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 100) {
            records { id createdDateTime assetType containerId }
          }
        }
      }
    `;
    const result = await fetchGraphQLApi<{
      temporalDataObject?: {
        assets?: {
          records?: Array<{
            id: string;
            createdDateTime?: string;
            assetType?: string | null;
            containerId?: string | null;
          }>;
        } | null;
      } | null;
    }>({
      endpoint: `${apiRoot}/${graphQLEndpoint}`,
      query,
      variables: { id: projectTdoId },
      token,
      veritoneAppId,
    });
    const records = result.data?.temporalDataObject?.assets?.records ?? [];
    const writtenAt = Date.parse(justWrittenAt);
    if (Number.isNaN(writtenAt)) {
      return;
    }
    // Never the one just written, whatever the ordering says: deleting the
    // live record would leave the set unwatermarked with every reader
    // reporting it current.
    const superseded = records
      .filter((r) => {
        if (r.id === justWrittenId || !r.createdDateTime) {
          return false;
        }
        // Fails closed on both: a record that will not say what it is, or
        // says it belongs somewhere else, is not one to delete.
        if (
          r.assetType !== PROJECT_REDACTION_ASSET_TYPE ||
          String(r.containerId) !== String(projectTdoId)
        ) {
          return false;
        }
        const at = Date.parse(r.createdDateTime);
        return !Number.isNaN(at) && at < writtenAt;
      })
      // Newest first, by our own reading rather than the server's ordering,
      // so the one kept below is the one meant to be kept.
      .sort(
        (a, b) =>
          Date.parse(b.createdDateTime ?? '') -
          Date.parse(a.createdDateTime ?? '')
      );
    const stale = superseded.slice(KEEP_SUPERSEDED_BODIES);
    for (const record of stale) {
      await serviceDeleteAsset(
        record.id,
        token,
        apiRoot,
        graphQLEndpoint,
        veritoneAppId
      );
    }
  } catch (err) {
    // Swallowed on purpose: the write succeeded and that is what the reviewer
    // asked for. A body that outlives its usefulness is not the newest and
    // changes nothing.
    console.warn(
      '[redact] merged document settings tidy-up skipped',
      err instanceof Error ? err.name : 'unknown error'
    );
  }
}
